import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Plus, FileText, Layers, Sparkles,
  GripVertical, Edit3, Copy, Trash2, Check, X,
  ChevronDown, ChevronRight,
} from 'lucide-react';
import {
  DndContext,
  DragEndEvent,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// ─── Types ────────────────────────────────────────────────────────────────────

type EditorBlock = {
  id: string;
  type: string;
  content: Record<string, unknown>;
};

type EditorPage = {
  id: string;
  name: string;
  blocks: EditorBlock[];
};

type NavSection = {
  markerId: string;
  title: string;
  pageId: string;
  pageName: string;
  pageIndex: number;
};

export type AIPreselection =
  | { type: 'page'; pageId: string }
  | { type: 'section'; markerId: string; pageId: string }
  | null;

export type PageNavigatorPanelProps = {
  pages: EditorPage[];
  currentPageId: string;
  onSwitchPage: (pageId: string) => void;
  onAddPage: () => void;
  onDuplicatePage: (pageId: string) => void;
  onDeletePage: (pageId: string) => void;
  onRenamePage: (pageId: string, newName: string) => void;
  onDragEnd: (event: DragEndEvent) => void;
  onAITrigger: (preselection: AIPreselection) => void;
  canDeletePage: boolean;
};

// ─── Sortable page row ────────────────────────────────────────────────────────

type SortableNavPageItemProps = {
  page: EditorPage;
  index: number;
  isActive: boolean;
  canDelete: boolean;
  onSwitch: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onRename: (name: string) => void;
  onAITrigger: () => void;
};

const SortableNavPageItem: React.FC<SortableNavPageItemProps> = React.memo(({
  page,
  index,
  isActive,
  canDelete,
  onSwitch,
  onDuplicate,
  onDelete,
  onRename,
  onAITrigger,
}) => {
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(page.name);
  const [hovered, setHovered] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: page.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 50 : undefined,
  };

  useEffect(() => {
    if (!isRenaming) setRenameValue(page.name);
  }, [page.name, isRenaming]);

  useEffect(() => {
    if (isRenaming && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isRenaming]);

  const commitRename = useCallback(() => {
    const trimmed = renameValue.trim();
    if (trimmed) onRename(trimmed);
    setIsRenaming(false);
  }, [renameValue, onRename]);

  const cancelRename = useCallback(() => {
    setRenameValue(page.name);
    setIsRenaming(false);
  }, [page.name]);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group flex items-center gap-1.5 rounded-lg px-2 py-1.5 cursor-pointer transition-colors ${
        isActive
          ? 'bg-blue-50 border border-blue-200'
          : 'hover:bg-gray-50 border border-transparent'
      }`}
      onClick={() => !isRenaming && onSwitch()}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onDoubleClick={(e) => { e.stopPropagation(); setIsRenaming(true); }}
    >
      {/* Drag handle */}
      <span
        {...attributes}
        {...listeners}
        className="flex-shrink-0 cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500"
        onClick={(e) => e.stopPropagation()}
      >
        <GripVertical className="w-3.5 h-3.5" />
      </span>

      {/* Page number badge */}
      <span className={`flex-shrink-0 w-5 h-5 rounded text-xs font-bold flex items-center justify-center ${
        isActive ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-600'
      }`}>
        {index + 1}
      </span>

      {/* Name / rename input */}
      {isRenaming ? (
        <input
          ref={inputRef}
          value={renameValue}
          onChange={(e) => setRenameValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitRename();
            if (e.key === 'Escape') cancelRename();
          }}
          onBlur={commitRename}
          onClick={(e) => e.stopPropagation()}
          className="flex-1 min-w-0 text-xs bg-white border border-blue-400 rounded px-1 py-0.5 outline-none"
        />
      ) : (
        <span className={`flex-1 min-w-0 text-xs truncate ${isActive ? 'text-blue-700 font-medium' : 'text-gray-700'}`}>
          {page.name}
        </span>
      )}

      {/* Action buttons — show on hover */}
      <AnimatePresence>
        {hovered && !isRenaming && (
          <motion.div
            initial={{ opacity: 0, width: 0 }}
            animate={{ opacity: 1, width: 'auto' }}
            exit={{ opacity: 0, width: 0 }}
            className="flex items-center gap-0.5 flex-shrink-0 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              title="AI Regenerate"
              onClick={onAITrigger}
              className="p-0.5 rounded hover:bg-blue-100 text-blue-400 hover:text-blue-600 transition-colors"
            >
              <Sparkles className="w-3 h-3" />
            </button>
            <button
              title="Rename (or double-click)"
              onClick={() => setIsRenaming(true)}
              className="p-0.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <Edit3 className="w-3 h-3" />
            </button>
            <button
              title="Duplicate page"
              onClick={onDuplicate}
              className="p-0.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <Copy className="w-3 h-3" />
            </button>
            {canDelete && (
              <button
                title="Delete page"
                onClick={onDelete}
                className="p-0.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Confirm / cancel when renaming */}
      {isRenaming && (
        <div className="flex items-center gap-0.5 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
          <button onClick={commitRename} className="p-0.5 rounded hover:bg-gray-100 text-green-500">
            <Check className="w-3 h-3" />
          </button>
          <button onClick={cancelRename} className="p-0.5 rounded hover:bg-gray-100 text-gray-400">
            <X className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  );
});

SortableNavPageItem.displayName = 'SortableNavPageItem';

// ─── Main component ───────────────────────────────────────────────────────────

export const PageNavigatorPanel: React.FC<PageNavigatorPanelProps> = ({
  pages,
  currentPageId,
  onSwitchPage,
  onAddPage,
  onDuplicatePage,
  onDeletePage,
  onRenamePage,
  onDragEnd,
  onAITrigger,
  canDeletePage,
}) => {
  const [activeTab, setActiveTab] = useState<'pages' | 'sections'>('pages');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedPageGroups, setExpandedPageGroups] = useState<Set<string>>(new Set(pages.map(p => p.id)));

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  // ── Compute sections from section-marker blocks ──────────────────────────
  const allSections = useMemo<NavSection[]>(() => {
    const result: NavSection[] = [];
    pages.forEach((page, pageIndex) => {
      page.blocks.forEach((block) => {
        if (block.type === 'section-marker') {
          result.push({
            markerId: block.id,
            title: (block.content as { title?: string }).title || 'Unnamed Section',
            pageId: page.id,
            pageName: page.name,
            pageIndex,
          });
        }
      });
    });
    return result;
  }, [pages]);

  // ── Filtered lists ───────────────────────────────────────────────────────
  const filteredPages = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return pages;
    return pages.filter(p => p.name.toLowerCase().includes(q));
  }, [pages, searchQuery]);

  const filteredSections = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return allSections;
    return allSections.filter(
      s => s.title.toLowerCase().includes(q) || s.pageName.toLowerCase().includes(q),
    );
  }, [allSections, searchQuery]);

  // Group filtered sections by page
  const sectionsByPage = useMemo(() => {
    const map = new Map<string, NavSection[]>();
    filteredSections.forEach(s => {
      if (!map.has(s.pageId)) map.set(s.pageId, []);
      map.get(s.pageId)!.push(s);
    });
    return map;
  }, [filteredSections]);

  const togglePageGroup = useCallback((pageId: string) => {
    setExpandedPageGroups(prev => {
      const next = new Set(prev);
      if (next.has(pageId)) next.delete(pageId); else next.add(pageId);
      return next;
    });
  }, []);

  const tabCount = useMemo(() => ({
    pages: pages.length,
    sections: allSections.length,
  }), [pages.length, allSections.length]);

  return (
    <div className="flex flex-col h-full">
      {/* ── Tabs + Add Page ── */}
      <div className="flex items-stretch border-b border-gray-200 bg-white flex-shrink-0">
        <button
          onClick={() => setActiveTab('pages')}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium transition-colors ${
            activeTab === 'pages'
              ? 'text-blue-600 border-b-2 border-blue-500 bg-blue-50'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <FileText className="w-3.5 h-3.5" />
          Pages
          <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${
            activeTab === 'pages' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
          }`}>
            {tabCount.pages}
          </span>
        </button>
        <button
          onClick={() => setActiveTab('sections')}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium transition-colors ${
            activeTab === 'sections'
              ? 'text-purple-600 border-b-2 border-purple-500 bg-purple-50'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Layers className="w-3.5 h-3.5" />
          Sections
          <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${
            activeTab === 'sections' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-500'
          }`}>
            {tabCount.sections}
          </span>
        </button>
      </div>

      {/* ── Search ── */}
      <div className="px-2 pt-2 pb-1 flex-shrink-0">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={activeTab === 'pages' ? 'Search pages…' : 'Search sections…'}
            className="w-full pl-6 pr-2 py-1.5 text-xs border border-gray-200 rounded-md bg-gray-50 focus:outline-none focus:border-blue-400 focus:bg-white transition-colors"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>

      {/* ── Content ── */}
      <div className="flex-1 overflow-y-auto px-2 pb-2">
        <AnimatePresence mode="wait">

          {/* ═══ PAGES TAB ════════════════════════════════════════════════════ */}
          {activeTab === 'pages' && (
            <motion.div
              key="pages"
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -8 }}
              transition={{ duration: 0.15 }}
            >
              {filteredPages.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-4">No pages match your search.</p>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={onDragEnd}
                >
                  <SortableContext
                    items={filteredPages.map(p => p.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-0.5 pt-1">
                      {filteredPages.map((page, idx) => {
                        const realIndex = pages.findIndex(p => p.id === page.id);
                        return (
                          <SortableNavPageItem
                            key={page.id}
                            page={page}
                            index={realIndex}
                            isActive={page.id === currentPageId}
                            canDelete={canDeletePage}
                            onSwitch={() => onSwitchPage(page.id)}
                            onDuplicate={() => onDuplicatePage(page.id)}
                            onDelete={() => onDeletePage(page.id)}
                            onRename={(name) => onRenamePage(page.id, name)}
                            onAITrigger={() => onAITrigger({ type: 'page', pageId: page.id })}
                          />
                        );
                      })}
                    </div>
                  </SortableContext>
                </DndContext>
              )}

              {/* ── New Page button ── */}
              <button
                onClick={onAddPage}
                className="w-full mt-2 flex items-center justify-center gap-1.5 py-2 rounded-lg border border-dashed border-blue-300 text-blue-500 hover:bg-blue-50 hover:border-blue-400 transition-colors text-xs font-medium"
              >
                <Plus className="w-3.5 h-3.5" />
                New Page
              </button>
            </motion.div>
          )}

          {/* ═══ SECTIONS TAB ═════════════════════════════════════════════════ */}
          {activeTab === 'sections' && (
            <motion.div
              key="sections"
              initial={{ opacity: 0, x: 8 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 8 }}
              transition={{ duration: 0.15 }}
              className="pt-1 space-y-1"
            >
              {allSections.length === 0 && (
                <div className="text-center py-6 text-gray-400">
                  <Layers className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-xs">No sections yet.</p>
                  <p className="text-xs mt-1 text-gray-300">Add a Section Divider block to create sections.</p>
                </div>
              )}

              {allSections.length > 0 && filteredSections.length === 0 && (
                <p className="text-xs text-gray-400 text-center py-4">No sections match your search.</p>
              )}

              {pages.map((page) => {
                const pageSections = sectionsByPage.get(page.id);
                if (!pageSections || pageSections.length === 0) return null;
                const isExpanded = expandedPageGroups.has(page.id);

                return (
                  <div key={page.id} className="rounded-lg overflow-hidden border border-gray-100">
                    {/* Page group header */}
                    <button
                      onClick={() => togglePageGroup(page.id)}
                      className={`w-full flex items-center gap-2 px-2 py-1.5 text-left transition-colors ${
                        page.id === currentPageId ? 'bg-blue-50' : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                    >
                      {isExpanded
                        ? <ChevronDown className="w-3 h-3 text-gray-400 flex-shrink-0" />
                        : <ChevronRight className="w-3 h-3 text-gray-400 flex-shrink-0" />
                      }
                      <FileText className={`w-3 h-3 flex-shrink-0 ${page.id === currentPageId ? 'text-blue-500' : 'text-gray-400'}`} />
                      <span className={`text-xs font-medium truncate flex-1 ${page.id === currentPageId ? 'text-blue-700' : 'text-gray-600'}`}>
                        {page.name}
                      </span>
                      <span className="text-xs text-gray-400 flex-shrink-0">{pageSections.length}</span>
                    </button>

                    {/* Section rows */}
                    <AnimatePresence initial={false}>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0 }}
                          animate={{ height: 'auto' }}
                          exit={{ height: 0 }}
                          transition={{ duration: 0.15 }}
                          className="overflow-hidden bg-white"
                        >
                          {pageSections.map((section) => (
                            <SectionNavItem
                              key={section.markerId}
                              section={section}
                              isPageActive={page.id === currentPageId}
                              onNavigate={() => onSwitchPage(page.id)}
                              onAITrigger={() => onAITrigger({
                                type: 'section',
                                markerId: section.markerId,
                                pageId: page.id,
                              })}
                            />
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

    </div>
  );
};

// ─── Section nav item ─────────────────────────────────────────────────────────

type SectionNavItemProps = {
  section: NavSection;
  isPageActive: boolean;
  onNavigate: () => void;
  onAITrigger: () => void;
};

const SectionNavItem: React.FC<SectionNavItemProps> = React.memo(({
  section,
  isPageActive,
  onNavigate,
  onAITrigger,
}) => {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-gray-50 transition-colors"
      onClick={onNavigate}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* indent line */}
      <div className="w-px h-3 bg-gray-200 ml-1 flex-shrink-0" />
      <Layers className="w-3 h-3 text-purple-400 flex-shrink-0" />
      <span className="flex-1 min-w-0 text-xs text-gray-600 truncate">{section.title}</span>
      <AnimatePresence>
        {hovered && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            onClick={(e) => { e.stopPropagation(); onAITrigger(); }}
            title="AI Regenerate this section"
            className="p-0.5 rounded hover:bg-purple-100 text-purple-400 hover:text-purple-600 transition-colors"
          >
            <Sparkles className="w-3 h-3" />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
});

SectionNavItem.displayName = 'SectionNavItem';
